import { Ionicons } from '@expo/vector-icons';
import React from "react";
import { View, Text, StyleSheet, TouchableOpacity } from "react-native";
import { ViewProps } from 'react-native';
import tw from "twrnc";

import { t } from "../../../i18n/translations";
import { iqesColors } from "../../../shared/ui/iqesDesignSystem";
import { useLanguageStore } from "../../quiz-core/languageStore";


interface RoomHeaderProps extends ViewProps {
  onShare?: () => void;
  roomName: string;
  hostName: string;
  onLeave: () => void;
  roomCode?: string;
  status?: string;
  categoryName?: string;
  difficulty?: string;
}

const RoomHeader = ({
  roomName,
  hostName,
  onShare,
  onLeave,
  roomCode,
  status,
  categoryName,
  difficulty,
  ...rest
}: RoomHeaderProps) => {
  const { language } = useLanguageStore();
  return (
    <View
      style={styles.header}
      data-oid="m6bx:9."
      {...rest}
    >
      <Text style={styles.eyebrow}>
        {t('multiplayer_lobby.quizRoom', language)}
      </Text>
      <Text style={styles.title} data-oid="vxw-:dv">
        {roomName}
      </Text>
      <Text style={styles.hostInfo} data-oid="v7-4_8y">
        {t('multiplayer_lobby.hostLabel', language)}: {hostName ? hostName : '-'}
      </Text>
      {roomCode && (
        <Text style={styles.roomCodeText} data-oid="nmaltt2">
          {t('multiplayer_lobby.gameRoomCode', language)}: {roomCode}
        </Text>
      )}
      {status && (
        <Text style={styles.statusText} data-oid="_ezur95">
          {t('multiplayer_lobby.status', language)}:{" "}
          <Text style={tw`font-bold`} data-oid="pz.y1.n">
            {status}
          </Text>
        </Text>
      )}
      {categoryName && (
        <View style={styles.infoRow}>
          <Ionicons name="book-outline" size={15} color={iqesColors.goldSoft} style={styles.infoIcon} />
          <Text style={styles.categoryText} data-oid="06:a2wr">
            {t('multiplayer_lobby.category', language)}:{" "}
            <Text style={tw`font-bold`} data-oid="1o:vmbq">
              {categoryName}
            </Text>
          </Text>
        </View>
      )}
      {difficulty && (
        <View style={styles.infoRow}>
          <Ionicons name="speedometer-outline" size={15} color={iqesColors.goldSoft} style={styles.infoIcon} />
          <Text style={styles.difficultyText} data-oid="0w:em06">
            {t('multiplayer_lobby.difficulty', language)}:{" "}
            <Text style={tw`font-bold capitalize`} data-oid="yfk7sm0">
              {difficulty?.toLowerCase() || "easy"}
            </Text>
          </Text>
        </View>
      )}
      <View style={styles.actionsContainer} data-oid="-w3rr2:">
        {onShare && (
          <TouchableOpacity
            onPress={onShare}
            style={[styles.actionButton, styles.shareButton]}
            accessibilityLabel={t('multiplayer_lobby.shareInviteAccessibility', language)}
            accessibilityRole="button"
          >
            <View style={styles.buttonContent}>
              <Ionicons name="share-outline" size={16} color="#FFFFFF" style={styles.buttonIcon} />
              <Text style={styles.actionButtonText}>{t('multiplayer_lobby.shareInvite', language)}</Text>
            </View>
          </TouchableOpacity>
        )}
        <TouchableOpacity
          onPress={onLeave}
          style={[styles.actionButton, styles.leaveButton]}
          data-oid="4whb3l1"
        >
          <View style={styles.buttonContent}>
            <Ionicons name="exit-outline" size={16} color="#FFFFFF" style={styles.buttonIcon} />
            <Text style={styles.actionButtonText} data-oid="x04a4n1">
              {t('multiplayer_lobby.leaveRoom', language)}
            </Text>
          </View>
        </TouchableOpacity>
        
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  header: {
    marginBottom: 20,
    paddingHorizontal: 20,
    paddingVertical: 20,
    borderRadius: 22,
    borderWidth: 1,
    alignItems: "center",
    backgroundColor: 'rgba(8, 10, 24, 0.92)',
    borderColor: 'rgba(246, 190, 58, 0.45)',
    shadowColor: iqesColors.gold,
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.18,
    shadowRadius: 16,
    elevation: 8,
  },
  eyebrow: {
    color: iqesColors.goldSoft,
    fontSize: 12,
    fontWeight: '900',
    letterSpacing: 1.5,
    textTransform: 'uppercase',
    marginBottom: 8,
  },
  title: {
    color: iqesColors.ivory,
    fontSize: 27,
    lineHeight: 32,
    fontWeight: "900",
    marginBottom: 8,
    textAlign: 'center',
  },
  hostInfo: {
    color: iqesColors.mutedWarm,
    fontSize: 17,
    marginBottom: 10,
    fontWeight: "700",
  },
  roomCodeText: {
    color: iqesColors.goldSoft,
    fontSize: 16,
    marginBottom: 6,
    fontWeight: "800",
  },
  statusText: {
    color: iqesColors.mutedWarm,
    fontSize: 15,
    marginBottom: 6,
    fontWeight: "500",
  },
  categoryText: {
    color: iqesColors.mutedWarm,
    fontSize: 15,
    marginBottom: 6,
    fontWeight: "500",
  },
  difficultyText: {
    color: iqesColors.mutedWarm,
    fontSize: 15,
    marginBottom: 14,
    fontWeight: "500",
  },
  actionsContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-around",
    width: "100%",
    marginTop: 12,
  },
  actionButton: {
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 12,
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 1,
    minWidth: 110,
    shadowColor: '#1D1A23',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.06,
    shadowRadius: 3,
    elevation: 2,
  },
  shareButton: {
    backgroundColor: iqesColors.royalPurple,
    borderWidth: 1,
    borderColor: 'rgba(246, 190, 58, 0.36)',
  },
  leaveButton: {
    backgroundColor: 'rgba(122, 30, 30, 0.9)',
    borderWidth: 1,
    borderColor: 'rgba(248, 113, 113, 0.45)',
  },
  actionButtonText: {
    color: "#FFFFFF",
    fontWeight: "700",
    fontSize: 15,
  },
  infoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  infoIcon: {
    marginRight: 6,
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  buttonIcon: {
    marginRight: 6,
  },
});

export default RoomHeader;
